import { ChevronRight } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css, keyframes } from "styled-components";
import { scrollbars } from "theme/mixins";
import { H3R, H4M, H4R } from "typography/headers";
import { Modal, Table } from "ui/core";

import Filters from "Common/Filters";

export const NavContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${color.neutral[100]};
  border-bottom: 1px solid ${color.border.default};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0 3rem;
  z-index: 99;
`;

export const Logo = styled.img`
  width: 126px;
  height: 18px;
  line-height: 0;
  margin-right: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
`;

export const NavLine = styled.div`
  border-left: 1px solid ${color.neutral[300]};
  height: 16px;
  margin: 0px 16px;
`;

export const SwitcherButton = styled(H4R)<{
  $open: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  color: ${color.neutral[400]};

  &:hover {
    color: ${color.basic.black};
    text-decoration: underline;
    cursor: pointer;
  }

  ${(props) =>
    props.$open &&
    css`
      color: ${color.basic.black};
    `}

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.25;
      cursor: default !important;
    `}
`;

export const SwitcherChevron = styled(ChevronRight).attrs({
  "aria-hidden": true,
})<{
  $open: boolean;
}>`
  display: block;
  width: 20px;
  height: 20px;
  color: ${color.neutral[400]};
  transform: rotate(${(props) => (props.$open ? -90 : 90)}deg);
  transition: transform 0.2s;
  margin-left: 8px;
`;

const reveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

export const SwitcherDropdownMenuWrapper = styled.div.attrs({
  role: "dialog",
})<{
  $open: boolean;
  $top: number;
  $right: number;
}>`
  position: fixed;
  z-index: 99;
  top: ${({ $top }) => `${$top}px`};
  right: ${({ $right }) => `${$right}px`};
  margin-top: 8px;
  animation: ${reveal} 0.3s ease backwards;

  width: 280px;
  max-height: min(576px, 69vh);
  background: ${color.basic.white};
  border: 1px solid ${color.foreground.disabled};
  box-shadow: 0px 4px 8px rgba(0, 0, 8, 0.08);
  border-radius: 4px;

  display: ${(props) => (props.$open ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  padding: 8px 0px;
`;

export const SwitcherDropdownOptionsList = styled.div`
  max-height: 440px;
  overflow: auto;
  ${scrollbars()}
  // Offset to allow padding on scroll bar
  margin-right: -12px;
  &:empty {
    display: none;
  }
`;

export const ExpandableFilterWrapper = styled(Filters)`
  border: none;
  margin-bottom: 0;
`;

export const FilterText = styled(H4M)`
  color: ${color.neutral[400]};
  margin-right: 4px;
  ::after {
    content: ":";
  }
`;

export const HeadCell = styled(Table.HeadCell)<{
  $borderTopNone?: boolean;
  $noWrap?: boolean;
  $right?: boolean;
}>`
  background-color: ${color.basic.white};

  ${(props) =>
    props.$borderTopNone &&
    css`
      border-top: none;
    `}

  ${(props) =>
    props.$noWrap &&
    css`
      padding: 5px;
      border-top: none;

      > span {
        white-space: nowrap;
      }
    `}

  ${(props) =>
    props.$right &&
    css`
      justify-content: flex-end;
      text-align: right;
    `}
`;

export const ModifiedHeadCell = styled(HeadCell)`
  justify-content: start !important;
`;

export const ModifiedCondensedCell = styled(Table.CondensedCell)<{
  $isPositive?: boolean;
}>`
  padding: 5px;
  height: 54px;

  ${(props) =>
    props.$isPositive &&
    css`
      border: 1px solid ${color.green[300]};
    `};
`;

export const ModifiedActionCell = styled(Table.CondensedCell)<{
  $isPositive?: boolean;
}>`
  padding: 5px;
  height: 54px;
  justify-content: center;

  ${(props) =>
    props.$isPositive &&
    css`
      border: 1px solid ${color.green[300]};
    `};
`;

export const ModifiedActionHeadCell = styled(HeadCell)`
  justify-content: center !important;
`;

export const ListEntry = styled(Table.CondensedCell)`
  color: ${color.basic.black};
  border-left: 1px solid color.neutral[200];
  border-right: 1px solid color.neutral[200];

  &:first-of-type {
    border-top: 1px solid color.neutral[200];
    border-radius: 8px 8px 0px 0px;
  }

  &:last-of-type {
    border-radius: 0px 0px 8px 8px;
    margin-bottom: 24px;
  }

  &:only-of-type {
    border-radius: 8px;
  }
`;

export const DetailWrapper = styled.div`
  margin: 20px 0;
`;

export const DetailItem = styled("div")`
  display: flex;
  align-items: center;
  margin: 8px 0;
  flex-wrap: wrap;
`;

export const DetailItemName = styled(H3R)`
  color: ${color.neutral[400]};
  padding-right: 4px;
`;

export const DownloadFileSection = styled("div")<{ $disabled?: boolean }>`
  color: ${color.blue[300]};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  ${(props) =>
    props.$disabled &&
    css`
      color: ${color.neutral[400]};
      cursor: not-allowed;

      &:hover {
        text-decoration: none;
      }
    `}
`;
export const PreviewContainer = styled("div")<{ $disabled?: boolean }>`
  margin-top: 8px;
  margin-right: 8px;
  color: ${color.blue[300]};
  cursor: pointer;

  ${(props) =>
    props.$disabled &&
    css`
      color: ${color.neutral[400]};
      cursor: not-allowed;
    `}
`;
export const ActionableCellWrapper = styled.div`
  color: ${color.blue[300]};
  text-decoration: underline;
  cursor: pointer;
`;
export const ActionableIconCell = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
export const CellIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

export const RightAlignedFilter = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

export const ActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const FieldWrapper = styled.div<{ $flex?: boolean }>`
  margin: 20px 0;

  ${({ $flex }) =>
    $flex &&
    css`
      display: flex;

      > div {
        width: 50%;
      }
      > div:first-child {
        margin-right: 16px;
      }
    `}
`;

export const NavHeaderBackground = styled.div<{ $zIndex?: number }>`
  position: sticky;
  top: 0;
  width: 100%;
  height: 52px;
  border-bottom: 1px solid ${color.border.default};
  background-color: ${color.neutral[100]};
  z-index: ${(props) => props.$zIndex ?? 100};
`;

export const FilterVerticalLine = styled.div`
  border-left: 1px solid ${color.neutral[300]};
  height: 16px;
`;

export const Indicator = styled.div<{ $open: boolean }>`
  position: absolute;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border: 0;
  padding: 0;
  margin: 0;
  left: 12px;
  // top: 51.5%;
  margin-top: -12px;
  overflow: hidden;
  font-size: 10px;
  line-height: 24px;
  transform: rotate(0deg);
  transition: 0.4s ease;

  ${(props) =>
    props.$open &&
    css`
      transform: rotate(180deg);
    `}

  svg {
    width: 24px;
    height: 24px;
    color: ${color.blue[300]};
    transform: rotate(-90deg);
  }
`;

export const ShowMoreNumber = styled.span<{
  $textColor?: string;
  $cursorText?: boolean;
}>`
  color: ${color.blue[300]};
  text-decoration: underline;
  cursor: pointer;

  ${(props) =>
    props.$textColor &&
    css`
      color: ${props.$textColor};
    `}

  ${(props) =>
    props.$cursorText &&
    css`
      cursor: text;
    `}
`;

export const FilePreviewModalBody = styled(Modal.Body)`
  table,
  th,
  td {
    border: 1px solid ${color.border.default};
    border-collapse: collapse;
  }

  #msdoc-renderer {
    // unfortunately this is a bit of a 'magic' number as the underlying CSS collapses so we cant work with %'s etc
    height: 58vh;
  }
`;

export const Fade = styled.div<{
  direction?: "right" | "up" | "down";
  slow?: boolean;
}>`
  flex: 1;
  opacity: 0;
  ${(props) =>
    props.direction === "right"
      ? css`
          animation: fadeInRight ${props.slow ? "0.5s" : "0.25s"} ease-in-out
            forwards;
        `
      : props.direction === "down"
      ? css`
          animation: fadeInDown ${props.slow ? "0.5s" : "0.25s"} ease-in-out
            forwards;
        `
      : css`
          animation: fadeIn ${props.slow ? "0.5s" : "0.25s"} ease-in-out
            forwards;
        `}

  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(-0.5rem);
      transform-origin: left;
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(0.5rem) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;
